import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { MeInfo } from "../../shared/api-types";
import { apiJson } from "./api";
import { authClient } from "./auth";
import { confirmLeave } from "./leave-guard";

// /api/me einmal nach dem Login laden und überall verfügbar machen: aktiver
// Account + eigene Rolle darin, alle Accounts (Umschalter), Betreiber-Flag.

type MeState = { me: MeInfo | null; error: boolean; reload: () => Promise<void> };

const MeContext = createContext<MeState>({ me: null, error: false, reload: async () => {} });

export function MeProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const [me, setMe] = useState<MeInfo | null>(null);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    try {
      setMe(await apiJson<MeInfo>("/api/me"));
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  // userId als Abhängigkeit: nach Logout/Login als jemand anderes neu laden
  useEffect(() => {
    void reload();
  }, [reload, userId]);

  return <MeContext.Provider value={{ me, error, reload }}>{children}</MeContext.Provider>;
}

export function useMe(): MeState {
  return useContext(MeContext);
}

/**
 * Aktiven Account wechseln: Session serverseitig umstellen, dann komplett neu
 * laden — jede Seite hält Account-Daten im State, ein Reload ist der sichere Weg.
 * Laufende Uploads fragen VOR dem Umstellen (leave-guard) — der Browser-Dialog beim
 * Neuladen käme zu spät, die Sitzung wäre dann schon gewechselt.
 */
export async function switchAccount(accountId: string): Promise<void> {
  if (!(await confirmLeave())) return;
  const { error } = await authClient.organization.setActive({ organizationId: accountId });
  if (!error) window.location.assign("/");
}
